import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import {
  Box, Typography, Alert, Snackbar, IconButton, Paper, Button,
  Collapse, Chip, Stack, alpha, LinearProgress
} from '@mui/material';
import {
  ExpandMore, ChevronRight, Business, Groups, Group, Person,
  Refresh, FolderOpen, AccountTree, Computer, Memory, Storage, Speed
} from '@mui/icons-material';
import { useKeycloak } from '@react-keycloak/web';
import { getClientNodes } from '../api/services/computeService/ClientNodeService';
import { getDepartmentNodes } from '../api/services/computeService/DepartmentNodeService';
import { getTeamNodes } from '../api/services/computeService/TeamNodeService';
import { getNodeById, transformNodeToDisplayFormat } from '../api/services/computeService/NodeService';

// Helper function to format GPU information
const formatGpuInfo = (node) => {
  if (node.gpuBreakdown && node.gpuBreakdown.length > 0) {
    return node.gpuBreakdown.map(gpu => {
      // Extract vendor from GPU model
      let vendor = 'Unknown';
      const model = gpu.model.toLowerCase();
      if (model.includes('rtx') || model.includes('gtx') || model.includes('tesla') || model.includes('quadro') || model.includes('geforce')) {
        vendor = 'NVIDIA';
      } else if (model.includes('radeon') || model.includes('rx ')) {
        vendor = 'AMD';
      } else if (model.includes('intel') || model.includes('arc')) {
        vendor = 'Intel';
      }

      return `${gpu.count}x ${vendor} ${gpu.model}`;
    }).join(', ');
  } else if (node.numGpus && node.numGpus > 0) {
    return `${node.numGpus} GPU${node.numGpus !== 1 ? 's' : ''}`;
  }
  return '';
};

const ResourceItem = memo(({ icon: Icon, label, value, color = 'text.secondary' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
    <Icon sx={{ fontSize: 12, color: 'success.main' }} />
    <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem', minWidth: 35 }}>
      {label}:
    </Typography>
    <Typography variant="caption" color={color} sx={{ fontSize: '0.65rem' }}>
      {value}
    </Typography>
  </Box>
));

const NodeResourceDisplay = memo(({ node }) => (
  <Box sx={{ pl: 1, mt: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.65rem', mb: 0.3 }}>
      RESOURCES
    </Typography>
    <Box sx={{ pl: 0.5 }}>
      {formatGpuInfo(node) && (
        <ResourceItem icon={Speed} label="GPU" value={formatGpuInfo(node)} />
      )}
      {node.numOfCpus && node.numOfCpus > 0 && (
        <ResourceItem icon={Speed} label="CPU" value={`${node.numOfCpus} cores`} />
      )}
      {node.mem && node.mem > 0 && (
        <ResourceItem icon={Memory} label="RAM" value={`${Math.round(node.mem)}GB`} />
      )}
      {node.storage && node.storage > 0 && (
        <ResourceItem icon={Storage} label="Disk" value={node.storage >= 1000 ? `${(node.storage / 1000).toFixed(1)}TB` : `${node.storage}GB`} />
      )}
    </Box>
  </Box>
));

const TreeNode = memo(({
  label,
  icon: Icon,
  iconColor = 'primary.main',
  count,
  depth = 0,
  expanded,
  onToggle,
  hasChildren,
  children
}) => (
  <Box>
    <Box
      onClick={hasChildren ? onToggle : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1.5,
        pl: 1.5 + depth * 2.5,
        cursor: hasChildren ? 'pointer' : 'default',
        borderRadius: 1,
        transition: 'background-color 0.1s',
        '&:hover': hasChildren ? { bgcolor: 'action.hover' } : {}
      }}
    >
      {hasChildren ? (
        expanded ? <ExpandMore fontSize="small" color="action" /> : <ChevronRight fontSize="small" color="action" />
      ) : (
        <Box sx={{ width: 24 }} />
      )}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 0.75,
          bgcolor: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ fontSize: 16, color: 'white' }} />
      </Box>
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{label}</Typography>
      {count !== undefined && (
        <Chip label={count} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'action.hover' }} />
      )}
    </Box>
    {hasChildren && <Collapse in={expanded}>{children}</Collapse>}
  </Box>
));

const UserNode = memo(({ user, role, depth }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.5,
      px: 1.5,
      pl: 1.5 + depth * 2.5 + 24 + 8,
      borderRadius: 1,
      '&:hover': { bgcolor: 'action.hover' }
    }}
  >
    <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
      {user.fullName || user.email}
    </Typography>
    {role && (
      <Chip 
        label={role} 
        size="small" 
        variant="outlined"
        sx={{ height: 18, fontSize: '0.65rem' }} 
      />
    )}
  </Box>
));

export default function HierarchyView({ userPermissions, sharedData, onRefresh }) {
  const { keycloak } = useKeycloak();
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [nodeAssignments, setNodeAssignments] = useState({
    clients: {},
    departments: {},
    teams: {}
  });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const toggle = useCallback((id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const expandAll = useCallback(() => {
    const all = {};
    clients.forEach(c => all[`c-${c.id}`] = true);
    departments.forEach(d => all[`d-${d.id}`] = true);
    teams.forEach(t => all[`t-${t.id}`] = true);
    setExpanded(all);
  }, [clients, departments, teams]);

  const collapseAll = useCallback(() => setExpanded({}), []);

  const getUsersForTeam = useCallback((teamId) => {
    return users.filter(u => 
      u.teams?.some(t => t.id === teamId) || u.teamIds?.includes(teamId)
    );
  }, [users]);

  const getUsersForDepartment = useCallback((deptId) => {
    return users.filter(u => 
      (u.departments?.some(d => d.id === deptId) || u.departmentIds?.includes(deptId)) &&
      !u.teams?.length && !u.teamIds?.length
    );
  }, [users]);

  const getUsersForClient = useCallback((clientId) => {
    return users.filter(u =>
      (u.clients?.some(c => c.id === clientId) || u.clientIds?.includes(clientId)) &&
      !u.departments?.length && !u.departmentIds?.length &&
      !u.teams?.length && !u.teamIds?.length
    );
  }, [users]);

  const fetchNodeAssignments = useCallback(async () => {
    if (!keycloak?.token) return;

    try {
      await keycloak.updateToken(30);
      const assignments = {
        clients: {},
        departments: {},
        teams: {}
      };

      await Promise.all([
        ...clients.map(async (client) => {
          try {
            const response = await getClientNodes({ clientId: client.id, token: keycloak.token });
            console.log(`Client ${client.name} (${client.id}) node response:`, response);
            if (response.success && response.data?.nodeIds) {
              console.log(`Found ${response.data.nodeIds.length} node(s) for client ${client.name}`);
              const nodeDetails = await Promise.all(
                response.data.nodeIds.map(async (nodeId) => {
                  try {
                    const nodeResponse = await getNodeById(nodeId, keycloak.token);
                    console.log(`Node ${nodeId} response:`, nodeResponse);
                    if (nodeResponse.success && nodeResponse.data) {
                      const transformed = transformNodeToDisplayFormat(nodeResponse.data);
                      console.log(`Transformed node ${nodeId}:`, transformed);
                      return transformed;
                    }
                    console.warn(`Node ${nodeId} fetch failed or no data:`, nodeResponse);
                    return null;
                  } catch (error) {
                    console.warn(`Failed to fetch node ${nodeId}:`, error);
                    return null;
                  }
                })
              );
              const validNodes = nodeDetails.filter(n => n !== null);
              console.log(`Client ${client.name} final nodes count:`, validNodes.length);
              assignments.clients[client.id] = validNodes;
            } else {
              console.log(`No nodes found for client ${client.name}`);
              assignments.clients[client.id] = [];
            }
          } catch (error) {
            console.warn(`Failed to fetch nodes for client ${client.id}:`, error);
            assignments.clients[client.id] = [];
          }
        }),
        ...departments.map(async (dept) => {
          try {
            const response = await getDepartmentNodes({ departmentId: dept.id, token: keycloak.token });
            if (response.success && response.data.nodeIds) {
              const nodeDetails = await Promise.all(
                response.data.nodeIds.map(async (nodeId) => {
                  try {
                    const nodeResponse = await getNodeById(nodeId, keycloak.token);
                    if (nodeResponse.success && nodeResponse.data) {
                      return transformNodeToDisplayFormat(nodeResponse.data);
                    }
                    return null;
                  } catch (error) {
                    console.warn(`Failed to fetch node ${nodeId}:`, error);
                    return null;
                  }
                })
              );
              assignments.departments[dept.id] = nodeDetails.filter(n => n !== null);
            } else {
              assignments.departments[dept.id] = [];
            }
          } catch (error) {
            console.warn(`Failed to fetch nodes for department ${dept.id}:`, error);
            assignments.departments[dept.id] = [];
          }
        }),
        ...teams.map(async (team) => {
          try {
            const response = await getTeamNodes({ teamId: team.id, token: keycloak.token });
            if (response.success && response.data.nodeIds) {
              const nodeDetails = await Promise.all(
                response.data.nodeIds.map(async (nodeId) => {
                  try {
                    const nodeResponse = await getNodeById(nodeId, keycloak.token);
                    if (nodeResponse.success && nodeResponse.data) {
                      return transformNodeToDisplayFormat(nodeResponse.data);
                    }
                    return null;
                  } catch (error) {
                    console.warn(`Failed to fetch node ${nodeId}:`, error);
                    return null;
                  }
                })
              );
              assignments.teams[team.id] = nodeDetails.filter(n => n !== null);
            } else {
              assignments.teams[team.id] = [];
            }
          } catch (error) {
            console.warn(`Failed to fetch nodes for team ${team.id}:`, error);
            assignments.teams[team.id] = [];
          }
        })
      ]);

      setNodeAssignments(assignments);
    } catch (error) {
      console.error('Failed to fetch node assignments:', error);
    }
  }, [keycloak, clients, departments, teams]);

  const getAssignedNodesForClient = useCallback((clientId) => {
    return nodeAssignments.clients[clientId] || [];
  }, [nodeAssignments]);

  const getAssignedNodesForDepartment = useCallback((deptId) => {
    return nodeAssignments.departments[deptId] || [];
  }, [nodeAssignments]);

  const getAssignedNodesForTeam = useCallback((teamId) => {
    return nodeAssignments.teams[teamId] || [];
  }, [nodeAssignments]);

  useEffect(() => {
    if (sharedData) {
      const processedUsers = (sharedData.users || []).map(u => ({
        ...u,
        fullName: u.full_name || u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        clientIds: u.clients?.map(c => c.id) || [],
        departmentIds: u.departments?.map(d => d.id) || [],
        teamIds: u.teams?.map(t => t.id) || []
      }));

      setClients(sharedData.clients || []);
      setDepartments(sharedData.departments || []);
      setTeams(sharedData.teams || []);
      setUsers(processedUsers);
      setNodes(sharedData.nodes || []);
    }
  }, [sharedData]);

  useEffect(() => {
    if (clients.length > 0 || departments.length > 0 || teams.length > 0) {
      fetchNodeAssignments();
    }
  }, [fetchNodeAssignments, clients.length, departments.length, teams.length]);

  const orphanedUsers = useMemo(() => 
    users.filter(u => 
      !u.clients?.length && !u.clientIds?.length &&
      !u.departments?.length && !u.departmentIds?.length &&
      !u.teams?.length && !u.teamIds?.length
    ), [users]);

  const handleRefresh = async () => {
    setLoading(true);
    if (onRefresh) await onRefresh();
    await fetchNodeAssignments();
    setLoading(false);
    setNotification({ open: true, message: 'Refreshed', severity: 'success' });
  };

  const stats = useMemo(() => ({
    clients: clients.length,
    departments: departments.length,
    teams: teams.length,
    users: users.length,
    nodes: nodes.length
  }), [clients, departments, teams, users, nodes]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>Organization</Typography>
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary">{stats.clients} clients</Typography>
            <Typography variant="body2" color="text.secondary">{stats.departments} depts</Typography>
            <Typography variant="body2" color="text.secondary">{stats.teams} teams</Typography>
            <Typography variant="body2" color="text.secondary">{stats.users} users</Typography>
            <Typography variant="body2" color="text.secondary">{stats.nodes} nodes</Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={expandAll}>Expand</Button>
          <Button size="small" onClick={collapseAll}>Collapse</Button>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {clients.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <AccountTree sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No organization data</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ py: 1 }}>
          {clients.map(client => {
            const clientDepts = departments.filter(d => d.clientId === client.id);
            const clientUsers = getUsersForClient(client.id);
            const clientNodes = getAssignedNodesForClient(client.id);
            const clientId = `c-${client.id}`;
            const isExpanded = expanded[clientId];

            return (
              <TreeNode
                key={client.id}
                label={client.name}
                icon={Business}
                iconColor="primary.main"
                count={clientDepts.length}
                depth={0}
                expanded={isExpanded}
                onToggle={() => toggle(clientId)}
                hasChildren={clientDepts.length > 0 || clientUsers.length > 0 || clientNodes.length > 0}
              >
                {clientDepts.map(dept => {
                  const deptTeams = teams.filter(t => t.departmentId === dept.id);
                  const deptUsers = getUsersForDepartment(dept.id);
                  const deptNodes = getAssignedNodesForDepartment(dept.id);
                  const deptId = `d-${dept.id}`;
                  const isDeptExpanded = expanded[deptId];

                  return (
                    <TreeNode
                      key={dept.id}
                      label={dept.name}
                      icon={Groups}
                      iconColor="secondary.main"
                      count={deptTeams.length}
                      depth={1}
                      expanded={isDeptExpanded}
                      onToggle={() => toggle(deptId)}
                      hasChildren={deptTeams.length > 0 || deptUsers.length > 0 || deptNodes.length > 0}
                    >
                      {deptTeams.map(team => {
                        const teamUsers = getUsersForTeam(team.id);
                        const teamNodes = getAssignedNodesForTeam(team.id);
                        const teamId = `t-${team.id}`;
                        const isTeamExpanded = expanded[teamId];

                        return (
                          <TreeNode
                            key={team.id}
                            label={team.name}
                            icon={Group}
                            iconColor="info.main"
                            count={teamUsers.length}
                            depth={2}
                            expanded={isTeamExpanded}
                            onToggle={() => toggle(teamId)}
                            hasChildren={teamUsers.length > 0 || teamNodes.length > 0}
                          >
                            {teamUsers.map(user => {
                              const userTeam = user.teams?.find(t => t.id === team.id);
                              const role = userTeam?.role === 'lead' || userTeam?.role === 'admin' ? (userTeam.role === 'lead' ? 'Lead' : 'Admin') : null;
                              return <UserNode key={user.id} user={user} role={role} depth={3} />;
                            })}
                            {teamNodes.length > 0 && (
                              <Box sx={{ pl: 2.5 * 3 + 1.5, py: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 0.5 }}>
                                  Assigned Nodes ({teamNodes.length})
                                </Typography>
                                {teamNodes.slice(0, 3).map(node => (
                                  <Box key={node.id || node.nodeId} sx={{ pl: 4, mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Computer sx={{ fontSize: 14, color: 'info.main' }} />
                                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                        {node.hostname || node.name || 'Unnamed Node'}
                                      </Typography>
                                    </Box>
                                    <NodeResourceDisplay node={node} />
                                  </Box>
                                ))}
                                {teamNodes.length > 3 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ pl: 4, fontSize: '0.65rem' }}>
                                    ... and {teamNodes.length - 3} more nodes
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TreeNode>
                        );
                      })}
                      {deptUsers.length > 0 && (
                        <Box sx={{ pl: 2.5 * 3 + 1.5, py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                            Direct members
                          </Typography>
                          {deptUsers.map(user => {
                            const userDept = user.departments?.find(d => d.id === dept.id);
                            const role = userDept?.role === 'admin' ? 'Admin' : null;
                            return <UserNode key={user.id} user={user} role={role} depth={3} />;
                          })}
                        </Box>
                      )}

                      {deptNodes.length > 0 && (
                        <Box sx={{ pl: 2.5 * 2 + 1.5, py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 0.5 }}>
                            Assigned Nodes ({deptNodes.length})
                          </Typography>
                          {deptNodes.slice(0, 3).map(node => (
                            <Box key={node.id || node.nodeId} sx={{ pl: 4, mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Computer sx={{ fontSize: 14, color: 'info.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                                  {node.hostname || node.name || 'Unnamed Node'}
                                </Typography>
                              </Box>
                              <NodeResourceDisplay node={node} />
                            </Box>
                          ))}
                          {deptNodes.length > 3 && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 4, fontSize: '0.65rem' }}>
                              ... and {deptNodes.length - 3} more nodes
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TreeNode>
                  );
                })}
                {clientUsers.length > 0 && (
                  <Box sx={{ pl: 2.5 * 2 + 1.5, py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                      Direct members
                    </Typography>
                    {clientUsers.map(user => {
                      const userClient = user.clients?.find(c => c.id === client.id);
                      const role = userClient?.role === 'admin' ? 'Admin' : null;
                      return <UserNode key={user.id} user={user} role={role} depth={2} />;
                    })}
                  </Box>
                )}

                {clientNodes.length > 0 && (
                  <Box sx={{ pl: 2.5 * 1 + 1.5, py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mb: 0.5 }}>
                      Assigned Nodes ({clientNodes.length})
                    </Typography>
                    {clientNodes.slice(0, 3).map(node => (
                      <Box key={node.id || node.nodeId} sx={{ pl: 4, mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Computer sx={{ fontSize: 14, color: 'info.main' }} />
                          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                            {node.hostname || node.name || 'Unnamed Node'}
                          </Typography>
                        </Box>
                        <NodeResourceDisplay node={node} />
                      </Box>
                    ))}
                    {clientNodes.length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 4, fontSize: '0.65rem' }}>
                        ... and {clientNodes.length - 3} more nodes
                      </Typography>
                    )}
                  </Box>
                )}
              </TreeNode>
            );
          })}
        </Paper>
      )}

      {orphanedUsers.length > 0 && (
        <Paper 
          variant="outlined" 
          sx={{ 
            mt: 2, 
            py: 1,
            borderColor: 'warning.main',
            borderStyle: 'dashed'
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" fontWeight={500} color="warning.main">
              Unassigned ({orphanedUsers.length})
            </Typography>
          </Box>
          {orphanedUsers.map(user => (
            <UserNode key={user.id} user={user} depth={0} />
          ))}
        </Paper>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(p => ({ ...p, open: false }))}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
